<template>
  <div class="quiz-frame">
    <div class="screen" role="main">

      <div class="header">
        <button
          type="button"
          class="back-button"
          :disabled="!canGoBack"
          :aria-label="chrome.back"
          @click="$emit('back')"
        >←</button>
        <div class="header-actions">
          <button
            v-if="primerContent"
            type="button"
            class="primer-button"
            aria-haspopup="dialog"
            @click="primerOpen = true"
          >
            <span class="primer-icon" aria-hidden="true">i</span>
            {{ primerLabel }}
          </button>
          <button
            v-if="showQuit"
            type="button"
            class="quit-button"
            :aria-label="chrome.quit"
            @click="$emit('quit')"
          >{{ chrome.quit }}</button>
        </div>
      </div>

      <div v-if="showQuestionProgress" class="counter-row">
        <span class="question-counter" :class="{ 'is-dim': counterDim }">
          {{ counterText }}
        </span>
      </div>

      <div
        v-if="showQuestionProgress"
        class="progress-bar"
        role="progressbar"
        :aria-valuenow="questionIndex + 1"
        aria-valuemin="1"
        :aria-valuemax="totalQuestions"
        :aria-label="chrome.progress"
      >
        <div
          v-for="seg in displayedSegments"
          :key="seg.id"
          class="progress-segment"
        >
          <div
            v-if="seg.fill > 0"
            class="progress-segment-fill"
            :style="{ width: (seg.fill * 100) + '%' }"
          ></div>
        </div>
      </div>

      <!-- Section-level progress for the section_complete celebration:
           a dot per section plus an "X of Y sections" count, shown in
           place of the per-question counter. -->
      <div v-if="showSectionProgress" class="section-progress">
        <div
          :key="sectionKey"
          class="section-dots"
          role="progressbar"
          :aria-valuenow="sectionsDone"
          aria-valuemin="0"
          :aria-valuemax="sectionsTotal"
          :aria-label="chrome.progress"
        >
          <span
            v-for="(dot, i) in sectionDots"
            :key="dot.id"
            class="section-dot"
            :class="`is-${dot.state}`"
            :style="{ animationDelay: (0.15 + i * 0.09) + 's' }"
          ></span>
        </div>
        <p class="section-progress-text">{{ chrome.sections(displayedDone, sectionsTotal) }}</p>
      </div>

      <!-- Per-question content lives here: section label, title, prompt,
           and whatever type-specific body the renderer wants. -->
      <slot></slot>

      <div class="footer">
        <button
          type="button"
          class="next-button"
          :disabled="nextDisabled"
          @click="$emit('next')"
        >{{ isLast ? chrome.done : chrome.next }}</button>
      </div>

      <div
        v-if="primerOpen"
        class="primer-overlay"
        role="dialog"
        aria-labelledby="primer-title"
        @click.self="primerOpen = false"
      >
        <div class="primer-sheet">
          <div class="primer-sheet-header">
            <h2 class="primer-sheet-title" id="primer-title">{{ primerLabel }}</h2>
            <button
              type="button"
              class="primer-close"
              :aria-label="chrome.close"
              @click="primerOpen = false"
            >×</button>
          </div>
          <p class="primer-body">{{ primerContent }}</p>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
// Shared quiz mock-phone frame. Hosts the chrome that's identical across
// every question type's preview: beige page bg, white screen card,
// header (back + primer button), counter, segmented progress bar, footer
// "Weiter" / "Fertig" button, and the primer popover.
//
// Each renderer (NumericStepperRenderer, SingleChoiceRenderer, etc.)
// supplies the per-question content via the default slot — typically a
// .question-block (section + title + prompt) followed by a type-specific
// body (stepper widget, radio list, matrix).
//
// Used by the editor previews and (eventually) by the live quiz flow.

// Chrome strings localized for the four supported locales. Used for the
// counter, progress aria-label, footer button text, back-button aria,
// and primer close aria. The body content of each question is rendered
// via the default slot in whatever language the renderer wants.
const CHROME_DICT = {
  en: {
    counter: (i, n) => `Question ${i} of ${n}`,
    sections: (x, n) => `${x} of ${n} sections completed`,
    progress: 'Progress',
    back: 'Back',
    next: 'Next',
    done: 'Done',
    close: 'Close',
    quit: 'Quit',
  },
  de: {
    counter: (i, n) => `Frage ${i} von ${n}`,
    sections: (x, n) => `${x} von ${n} Abschnitten geschafft`,
    progress: 'Fortschritt',
    back: 'Zurück',
    next: 'Weiter',
    done: 'Fertig',
    close: 'Schliessen',
    quit: 'Beenden',
  },
  fr: {
    counter: (i, n) => `Question ${i} sur ${n}`,
    sections: (x, n) => `${x} sur ${n} sections terminées`,
    progress: 'Progression',
    back: 'Retour',
    next: 'Suivant',
    done: 'Terminé',
    close: 'Fermer',
    quit: 'Quitter',
  },
  it: {
    counter: (i, n) => `Domanda ${i} di ${n}`,
    sections: (x, n) => `${x} di ${n} sezioni completate`,
    progress: 'Avanzamento',
    back: 'Indietro',
    next: 'Avanti',
    done: 'Fine',
    close: 'Chiudi',
    quit: 'Esci',
  },
};

export default {
  name: 'QuizFrame',
  props: {
    questionIndex: {
      type: Number,
      default: 0,
    },
    totalQuestions: {
      type: Number,
      default: 0,
    },
    canGoBack: {
      type: Boolean,
      default: false,
    },
    isLast: {
      type: Boolean,
      default: false,
    },
    // Upper-right info button. Shown only when primerContent is non-empty.
    // primerLabel is the visible button text (defaults to "Info" for a
    // generic call-out); primerContent is the body shown in the bottom
    // sheet when tapped.
    primerLabel: {
      type: String,
      default: 'Info',
    },
    primerContent: {
      type: String,
      default: '',
    },
    // Which language to render the chrome in (counter, footer button,
    // aria labels). Defaults to English so an unconfigured caller doesn't
    // see German chrome by accident.
    locale: {
      type: String,
      default: 'en',
    },
    // Show the Quit button in the header (right of the primer button).
    // The QuizRuntime engine passes true; the editor preview renderers
    // omit it (default false) so their chrome is unchanged.
    showQuit: {
      type: Boolean,
      default: false,
    },
    // Optional engine-supplied progress segments. Each entry is
    // { id: string, fill: number 0..1 }. When provided, the frame
    // renders them equal-width with fractional fill — used by the
    // QuizRuntime engine to draw one segment per section instead of
    // one segment per item. When null, the frame falls back to the
    // legacy per-question segment build driven by totalQuestions.
    // This keeps editor preview renderers working unchanged.
    progressSegments: {
      type: Array,
      default: null,
    },
    // When true, the "Question N of M" counter is rendered with a
    // muted style. The engine sets this on non-question items
    // (content_card, flow_card) so the counter persists for continuity
    // but visibly signals "between questions".
    counterDim: {
      type: Boolean,
      default: false,
    },
    // Disables the footer Next/Done control. The engine sets this when
    // the active card is gating and not yet satisfied (e.g. consent not
    // given). Default false so editor preview renderers are unchanged.
    nextDisabled: {
      type: Boolean,
      default: false,
    },
    // Which progress chrome to show: 'question' (per-question counter +
    // segmented bar), 'section' (section dots + "X of Y sections", for
    // the section_complete celebration), or 'none' (onboarding/wrap-up).
    // Default 'question' so editor preview renderers are unchanged.
    progressMode: {
      type: String,
      default: 'question',
    },
    // Section-progress inputs, used only when progressMode === 'section'.
    // sectionDots: [{ id, state: 'done' | 'current' | 'upcoming' }].
    sectionDots: {
      type: Array,
      default: () => [],
    },
    sectionsDone: {
      type: Number,
      default: 0,
    },
    sectionsTotal: {
      type: Number,
      default: 0,
    },
  },
  emits: ['back', 'next', 'quit'],
  data() {
    return {
      primerOpen: false,
      // The section count animates up from 0 on each section_complete
      // card (the "meter filling" dopamine). Reflects sectionsDone once
      // the count-up finishes.
      displayedDone: 0,
    };
  },
  computed: {
    showQuestionProgress() {
      return this.progressMode === 'question' && this.totalQuestions > 0;
    },
    showSectionProgress() {
      return this.progressMode === 'section' && this.sectionsTotal > 0;
    },
    /**
     * Final segment list rendered in the progress bar. When the parent
     * passes `progressSegments` explicitly (engine path), we use them
     * verbatim. Otherwise fall back to one segment per question — the
     * legacy mode used by the editor preview renderers, where past +
     * current are filled and future are empty.
     */
    displayedSegments() {
      if (Array.isArray(this.progressSegments)) {
        return this.progressSegments;
      }
      const total = Math.max(0, this.totalQuestions);
      return Array.from({ length: total }, (_, i) => ({
        id: `q-${i}`,
        fill: i <= this.questionIndex ? 1 : 0,
      }));
    },
    chrome() {
      return CHROME_DICT[this.locale] || CHROME_DICT.en;
    },
    counterText() {
      return this.chrome.counter(this.questionIndex + 1, this.totalQuestions);
    },
    // Identity of the current section-progress render. Changes when we
    // land on a (different) section card, which both replays the dot
    // pop-in (via :key) and retriggers the count-up (via the watcher).
    sectionKey() {
      return `${this.progressMode}|${this.sectionsDone}|${this.sectionsTotal}`;
    },
  },
  watch: {
    sectionKey: {
      immediate: true,
      handler() {
        this._animateSectionCount();
      },
    },
  },
  beforeUnmount() {
    if (this._countRaf) cancelAnimationFrame(this._countRaf);
  },
  methods: {
    _prefersReducedMotion() {
      try {
        return !!(window.matchMedia
          && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
      } catch (e) {
        return false;
      }
    },
    // Tween displayedDone 0 → sectionsDone with an ease-out, so the
    // "X of Y sections" reads like a meter counting up. Instant under
    // reduced motion (or when there's nothing to count).
    _animateSectionCount() {
      if (this._countRaf) {
        cancelAnimationFrame(this._countRaf);
        this._countRaf = null;
      }
      const target = this.progressMode === 'section' ? this.sectionsDone : 0;
      if (target <= 0 || this._prefersReducedMotion()) {
        this.displayedDone = target;
        return;
      }
      const duration = 800;
      const start = performance.now();
      this.displayedDone = 0;
      const tick = (now) => {
        const t = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - t, 3);
        this.displayedDone = Math.round(eased * target);
        if (t < 1) {
          this._countRaf = requestAnimationFrame(tick);
        } else {
          this.displayedDone = target;
          this._countRaf = null;
        }
      };
      this._countRaf = requestAnimationFrame(tick);
    },
  },
};
</script>

<style scoped>
.quiz-frame {
  --color-bg-page: #f5f4ef;
  --color-bg-card: #ffffff;
  --color-bg-muted: #f1efe8;
  --color-text-primary: #2c2c2a;
  --color-text-secondary: #5f5e5a;
  --color-text-tertiary: #888780;
  --color-border: rgba(0, 0, 0, 0.15);
  --color-accent: #534AB7;
  --color-accent-light: #EEEDFE;
  --radius-md: 8px;
  --radius-lg: 12px;
  --font-sans: 'Montserrat', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;

  background: var(--color-bg-page);
  font-family: var(--font-sans);
  color: var(--color-text-primary);
  line-height: 1.5;
  padding: 1.25rem;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  box-sizing: border-box;
  min-height: 100%;
  border-radius: var(--radius-lg);
}

.quiz-frame *,
.quiz-frame *::before,
.quiz-frame *::after {
  box-sizing: border-box;
}

.screen {
  width: 100%;
  max-width: 420px;
  background: var(--color-bg-card);
  border-radius: var(--radius-lg);
  border: 0.5px solid var(--color-border);
  padding: 1rem 1.25rem;
  min-height: 580px;
  display: flex;
  flex-direction: column;
  position: relative;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
  min-height: 28px;
}

.back-button {
  background: transparent;
  border: none;
  padding: 4px 8px;
  cursor: pointer;
  font-size: 20px;
  color: var(--color-text-secondary);
  line-height: 1;
}

.back-button:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.primer-button {
  background: var(--color-accent-light);
  border: 0.5px solid var(--color-accent);
  border-radius: var(--radius-md);
  padding: 6px 10px;
  font-size: 12px;
  color: var(--color-accent);
  cursor: pointer;
  font-family: inherit;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
}

.primer-button:hover {
  background: var(--color-accent);
  color: white;
}

.primer-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--color-accent);
  color: white;
  font-size: 10px;
  font-style: italic;
  font-family: Georgia, serif;
  line-height: 1;
}

.primer-button:hover .primer-icon {
  background: white;
  color: var(--color-accent);
}

/* Quit button — muted, never primary. Visible only in the engine
   (showQuit prop). Sits to the right of the primer button. */
.quit-button {
  background: transparent;
  border: 0.5px solid transparent;
  border-radius: var(--radius-md);
  padding: 6px 10px;
  font-size: 12px;
  color: var(--color-text-secondary, #5f5e5a);
  cursor: pointer;
  font-family: inherit;
  font-weight: 500;
}

.quit-button:hover {
  color: var(--color-bad, #C8392E);
  background: rgba(200, 57, 46, 0.08);
  border-color: rgba(200, 57, 46, 0.18);
}

.counter-row {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 0.75rem;
}

.question-counter {
  font-size: 12px;
  color: var(--color-text-secondary);
  transition: opacity 0.2s, color 0.2s;
}

/* Dimmed counter — used by the engine on non-question items so the
   counter still gives continuity ("Question 12 of 50") but visually
   signals "you're between questions". */
.question-counter.is-dim {
  opacity: 0.45;
  font-style: italic;
}

.progress-bar {
  display: flex;
  gap: 4px;
  margin-bottom: 1.5rem;
}

.progress-segment {
  flex: 1;
  height: 6px;
  border-radius: 3px;
  background: var(--color-bg-muted);
  overflow: hidden;
}

.progress-segment-fill {
  height: 100%;
  background: var(--color-accent);
  border-radius: 3px;
  transition: width 0.25s ease;
}

/* ───────── Section progress (section_complete celebration) ───────── */
.section-progress {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-bottom: 1.5rem;
}

.section-dots {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.section-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--color-bg-muted);
  border: 0.5px solid var(--color-border);
  transition: background 0.3s, border-color 0.3s, box-shadow 0.3s, width 0.3s, height 0.3s;
  animation: section-dot-pop 0.4s both;
}

@keyframes section-dot-pop {
  0%   { transform: scale(0); opacity: 0; }
  60%  { transform: scale(1.35); }
  100% { transform: scale(1); opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
  .section-dot { animation: none; }
}

.section-dot.is-done {
  background: #2D8659;
  border-color: #2D8659;
}

.section-dot.is-current {
  background: var(--color-accent);
  border-color: var(--color-accent);
  width: 12px;
  height: 12px;
  box-shadow: 0 0 0 3px rgba(83, 74, 183, 0.15);
}

.section-progress-text {
  font-size: 13px;
  color: var(--color-text-secondary);
  font-weight: 600;
  margin: 0;
}

.footer {
  margin-top: auto;
}

.next-button {
  width: 100%;
  padding: 14px;
  background: var(--color-accent);
  color: white;
  border: 0.5px solid var(--color-accent);
  border-radius: var(--radius-md);
  font-size: 15px;
  font-family: inherit;
  cursor: pointer;
  transition: opacity 0.15s;
}

.next-button:hover:not(:disabled) {
  opacity: 0.9;
}

.next-button:disabled {
  background: var(--color-bg-muted);
  color: var(--color-text-tertiary);
  border-color: var(--color-border);
  cursor: not-allowed;
}

.primer-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  border-radius: var(--radius-lg);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 10;
}

.primer-sheet {
  background: var(--color-bg-card);
  border-radius: var(--radius-lg) var(--radius-lg) 0 0;
  width: 100%;
  padding: 1.25rem;
  max-height: 80%;
  overflow-y: auto;
}

.primer-sheet-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 0.75rem;
  gap: 12px;
}

.primer-sheet-title {
  font-size: 16px;
  font-weight: 500;
  margin: 0;
  line-height: 1.3;
}

.primer-close {
  background: transparent;
  border: none;
  font-size: 22px;
  color: var(--color-text-secondary);
  cursor: pointer;
  line-height: 1;
  padding: 0 4px;
}

.primer-body {
  margin: 0;
  font-size: 14px;
  color: var(--color-text-primary);
  line-height: 1.5;
  white-space: pre-line;
}
</style>
